import React, { useState } from 'react';

import { Row, Col } from 'antd';
import { Formik, Field } from 'formik';

import styled from 'styled-components';

import { getMoment, getTime, isAllDay, timeArrayToString } from '@libs/NwMoment';

import {
    useUpdateGroupedIndividualFollowUp,
    useUpdatePeriodGroupedIndividualFollowUp,
} from '@hooks/API/Event/UseFollowup';

import TimeRangePicker from '@components/EventDateTimePicker/TimeRangePicker';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import { NWTextAreaCharCounter, NWSwitch } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

import { useFollowUpState } from './FollowUpContext';

const Container = styled.div``;

const FollowUpFormEditGroupInstance = ({ onTouchForm, onUpdateFollowupData, viewMode }) => {
    const { afterUpdate, editGroupInstance, groupType, onCancel } = useFollowUpState();
    const { mutateAsync: onUpdateGroupedIndividualFollowUp } = useUpdateGroupedIndividualFollowUp();
    const { mutateAsync: onUpdatePeriodGroupedIndividualFollowUp } = useUpdatePeriodGroupedIndividualFollowUp();

    const startMoment = getMoment(editGroupInstance.StartDate);
    const endMoment = getMoment(editGroupInstance.EndDate);
    const isOriginallyAllDay = isAllDay(startMoment, endMoment);

    const [allDay, setAllDay] = useState(isOriginallyAllDay);

    const initialValues = {
        Description: editGroupInstance.Description,
        StartDate: startMoment,
        EndDate: endMoment,
        StartTime: getTime(startMoment),
        EndTime: getTime(endMoment),
        allDay: isOriginallyAllDay,
        ChangeOnAllInstances: false,
    };

    const submitForm = async (values, setStatus, setSubmitting) => {
        // Build start and end dates with times
        const startTimeStr = timeArrayToString(values.StartTime);
        const endTimeStr = timeArrayToString(values.EndTime);
        const sd = getMoment(values.StartDate).format('YYYY-MM-DD') + ' ' + startTimeStr;
        const ed = getMoment(values.EndDate).format('YYYY-MM-DD') + ' ' + endTimeStr;

        const {
            StartDate: _sd1,
            EndDate: _ed1,
            StartTime: _st1,
            EndTime: _et1,
            allDay: _ad1,
            ...restInitialValues
        } = initialValues;
        const { StartDate: _sd2, EndDate: _ed2, StartTime: _st2, EndTime: _et2, allDay: _ad2, ...restValues } = values;

        const updatedFollowUpData = {
            ...restInitialValues,
            ...restValues,
            ID: editGroupInstance.ID,
            StartTime: sd,
            EndTime: ed,
        };

        try {
            // differenziare con PeriodGroup
            if (groupType === 'period') {
                updatedFollowUpData.ChangeOnAllDates = values.ChangeOnAllInstances;
                await onUpdatePeriodGroupedIndividualFollowUp({ followupData: updatedFollowUpData });
            } else {
                updatedFollowUpData.ChangeOnAllModels = values.ChangeOnAllInstances;
                await onUpdateGroupedIndividualFollowUp({ followupData: updatedFollowUpData });
            }
            if (afterUpdate) {
                afterUpdate();
            }
            onTouchForm(false);
            onUpdateFollowupData();
            if (!viewMode || viewMode === 'create') {
                handleCancel();
            }
        } catch (error) {
            console.error('Update Followup Error => ', error);
        }
        setSubmitting(false);
    };

    const beforeSubmit = async (values, setStatus, setSubmitting) => {
        await submitForm(values, setStatus, setSubmitting);
        if (onTouchForm) {
            onTouchForm(false);
        }
    };

    const handleCancel = () => {
        if (onTouchForm) {
            onTouchForm(false);
        }
        onCancel();
    };

    return (
        <Container className="event-form">
            <Formik
                initialValues={initialValues}
                enableReinitialize={false}
                // validationSchema={validationSchema}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    beforeSubmit(values, setStatus, setSubmitting);
                }}
            >
                {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                    <NwForm small values={values} onTouchForm={onTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Row gutter={10}>
                            <Col span={24}>
                                <Field
                                    component={NWTextAreaCharCounter}
                                    label="Description"
                                    name="Description"
                                    type="text"
                                    value={values.Description}
                                    maxLength={2000}
                                />
                            </Col>

                            {/* Time Range Picker with All Day Switch */}
                            <Col span={24}>
                                <NwDivider>Time</NwDivider>
                                <TimeRangePicker
                                    fields={{
                                        startTimeFieldName: 'StartTime',
                                        endTimeFieldName: 'EndTime',
                                        allDayFieldName: 'allDay',
                                    }}
                                    values={{
                                        startTime: values.StartTime,
                                        endTime: values.EndTime,
                                    }}
                                    methods={{
                                        setFieldValue: setFieldValue,
                                        onSetAllDay: setAllDay,
                                    }}
                                />
                            </Col>
                            {/* Change On All Models Switch */}
                            <Col span={24}>
                                <div style={{ margin: '2rem 0' }}>
                                    <Field
                                        component={NWSwitch}
                                        text={`apply these changes on all ${groupType === 'period' ? 'dates' : 'models'} in this activity`}
                                        name="ChangeOnAllInstances"
                                        value={values.ChangeOnAllInstances}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <NwFormButtonsBar
                            left={<NwCancelButton disabled={isSubmitting} onClick={handleCancel} />}
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            </Formik>
        </Container>
    );
};

export default React.memo(FollowUpFormEditGroupInstance);
